import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Globe, Shield, RefreshCw, Plus, Trash2, Edit2, Copy, 
  CheckCircle2, AlertCircle, Loader2, Server, ExternalLink,
  Clock, Calendar, Settings, Info, ArrowLeft, ChevronRight, XCircle
} from 'lucide-react';

export const PortalDomains = () => {
  const { token, user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  
  // Dialog states
  const [addRecordOpen, setAddRecordOpen] = useState(false);
  const [editRecordOpen, setEditRecordOpen] = useState(false);
  const [deleteRecordOpen, setDeleteRecordOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  
  // Settings states
  const [updatingSettings, setUpdatingSettings] = useState(false);
  
  // New record form
  const [newRecord, setNewRecord] = useState({
    name: '@',
    type: 'A',
    value: '',
    ttl: 3600,
    pref: 10
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Handle payment return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    const orderId = params.get('order_id');

    if (payment === 'success' && orderId && token) {
      // Confirm the domain order after successful payment
      const confirmOrder = async () => {
        try {
          const response = await fetch(`${backendUrl}/api/domain-orders/${orderId}/confirm`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              toast.success(`Domain ${result.domain} erfolgreich registriert!`);
            } else {
              toast.error(result.message || 'Domain-Registrierung fehlgeschlagen');
            }
          }
        } catch (err) {
          console.error('Error confirming domain order:', err);
        }
        // Clean URL
        window.history.replaceState({}, '', '/portal/domains');
      };
      confirmOrder();
    } else if (payment === 'cancelled') {
      toast.error('Zahlung abgebrochen');
      window.history.replaceState({}, '', '/portal/domains');
    }
  }, [token, backendUrl]);

  // Fetch domains
  useEffect(() => {
    const fetchDomains = async () => {
      if (!user?.id) return;
      
      try {
        const response = await fetch(`${backendUrl}/api/portal/domains?user_id=${user.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDomains(data.domains || []);
        }
      } catch (err) {
        console.error('Error fetching domains:', err);
        toast.error('Domains konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };

    fetchDomains();
  }, [token, user, backendUrl]);

  // Fetch DNS records when domain selected
  useEffect(() => {
    const fetchDNSRecords = async () => {
      if (!selectedDomain || !user?.id) return;
      
      setLoadingRecords(true);
      try {
        const response = await fetch(
          `${backendUrl}/api/portal/domains/${selectedDomain}/dns?user_id=${user.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.ok) {
          const data = await response.json();
          setDnsRecords(data.records || []);
        } else {
          setDnsRecords([]);
        }
      } catch (err) {
        console.error('Error fetching DNS records:', err);
        setDnsRecords([]);
      } finally {
        setLoadingRecords(false);
      }
    };

    fetchDNSRecords();
  }, [selectedDomain, token, user, backendUrl]);

  // Add DNS record
  const handleAddRecord = async () => {
    if (!newRecord.value) {
      toast.error('Bitte gib einen Wert ein');
      return;
    }

    try {
      const response = await fetch(
        `${backendUrl}/api/portal/domains/${selectedDomain}/dns?user_id=${user.id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newRecord)
        }
      );

      if (response.ok) {
        toast.success('DNS-Record erfolgreich hinzugefügt');
        setAddRecordOpen(false);
        setNewRecord({ name: '@', type: 'A', value: '', ttl: 3600, pref: 10 });
        // Refresh records
        const data = await fetch(
          `${backendUrl}/api/portal/domains/${selectedDomain}/dns?user_id=${user.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        ).then(r => r.json());
        setDnsRecords(data.records || []);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Hinzufügen');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    }
  };

  // Delete DNS record
  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;

    try {
      const response = await fetch(
        `${backendUrl}/api/portal/domains/${selectedDomain}/dns?user_id=${user.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: recordToDelete.name,
            type: recordToDelete.type,
            value: recordToDelete.value
          })
        }
      );

      if (response.ok) {
        toast.success('DNS-Record erfolgreich gelöscht');
        setDeleteRecordOpen(false);
        setRecordToDelete(null);
        setDnsRecords(prev => prev.filter(r => r.id !== recordToDelete.id));
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Löschen');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    }
  };

  // Update DNS record
  const handleUpdateRecord = async () => {
    if (!recordToEdit) return;

    try {
      const response = await fetch(
        `${backendUrl}/api/portal/domains/${selectedDomain}/dns?user_id=${user.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            old_name: recordToEdit.originalName,
            old_type: recordToEdit.originalType,
            old_value: recordToEdit.originalValue,
            new_name: recordToEdit.name,
            new_type: recordToEdit.type,
            new_value: recordToEdit.value,
            new_ttl: recordToEdit.ttl,
            new_pref: recordToEdit.pref
          })
        }
      );

      if (response.ok) {
        toast.success('DNS-Record erfolgreich aktualisiert');
        setEditRecordOpen(false);
        setRecordToEdit(null);
        const data = await fetch(
          `${backendUrl}/api/portal/domains/${selectedDomain}/dns?user_id=${user.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        ).then(r => r.json());
        setDnsRecords(data.records || []);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Aktualisieren');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    }
  };

  // Update domain settings (auto_renew)
  const handleUpdateDomainSettings = async (domain, setting, value) => {
    setUpdatingSettings(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/portal/domains/${domain}/settings?user_id=${user.id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ [setting]: value })
        }
      );

      if (response.ok) {
        // Update local state
        setDomains(prev => prev.map(d => 
          d.domain === domain ? { ...d, [setting]: value } : d
        ));
        toast.success(
          value 
            ? 'Automatische Verlängerung aktiviert' 
            : 'Automatische Verlängerung deaktiviert'
        );
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Fehler beim Speichern');
      }
    } catch (err) {
      toast.error('Verbindungsfehler');
    } finally {
      setUpdatingSettings(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
      case 'registered':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Aktiv</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">In Bearbeitung</Badge>;
      case 'expired':
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Abgelaufen</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getRecordTypeBadge = (type) => {
    const colors = {
      'A': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      'AAAA': 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      'CNAME': 'bg-green-500/10 text-green-500 border-green-500/20',
      'MX': 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      'TXT': 'bg-gray-500/10 text-gray-500 border-gray-500/20',
      'NS': 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
      'SRV': 'bg-pink-500/10 text-pink-500 border-pink-500/20',
      'CAA': 'bg-red-500/10 text-red-500 border-red-500/20',
      'ALIAS': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
    };
    return <Badge className={colors[type] || 'bg-gray-500/10'}>{type}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // No domains view
  if (domains.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            Domains
          </h1>
          <p className="text-muted-foreground">Verwalte deine Domains und DNS-Einstellungen</p>
        </div>
        
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Globe className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Keine Domains vorhanden</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Du hast noch keine Domain über Wolkenhelden gekauft. 
              Domains können beim Bestellen einer Cloud hinzugefügt werden.
            </p>
            <Button asChild>
              <a href="/checkout">Cloud bestellen</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Domain list view (overview)
  if (!selectedDomain) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            Domains
          </h1>
          <p className="text-muted-foreground">Verwalte deine Domains und DNS-Einstellungen</p>
        </div>

        <div className="grid gap-4">
          {domains.map(domain => (
            <Card 
              key={domain.domain} 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setSelectedDomain(domain.domain)}
              data-testid={`domain-card-${domain.domain}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Globe className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">{domain.domain}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        {getStatusBadge(domain.status)}
                        {domain.dns_record_type && (
                          <span className="text-xs text-muted-foreground">
                            DNS: {domain.dns_record_type}
                          </span>
                        )}
                        {domain.expires_at && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Läuft ab: {new Date(domain.expires_at).toLocaleDateString('de-DE')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Domain detail view
  const currentDomainData = domains.find(d => d.domain === selectedDomain);

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setSelectedDomain(null)}
          data-testid="back-to-domains-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6 text-primary" />
            {selectedDomain}
          </h1>
          <p className="text-muted-foreground">Domain-Verwaltung und DNS-Einstellungen</p>
        </div>
      </div>

      {currentDomainData && (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="dns">DNS-Records</TabsTrigger>
            <TabsTrigger value="settings">Einstellungen</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5 text-primary" />
                    Domain-Informationen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Status</span>
                    {getStatusBadge(currentDomainData.status)}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Registriert am</span>
                    <span>{currentDomainData.registered_at ? 
                      new Date(currentDomainData.registered_at).toLocaleDateString('de-DE') : 
                      '—'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Läuft ab am</span>
                    <span>{currentDomainData.expires_at ? 
                      new Date(currentDomainData.expires_at).toLocaleDateString('de-DE') : 
                      '—'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Auto-Verlängerung</span>
                    {currentDomainData.auto_renew !== false ? (
                      <Badge variant="outline" className="bg-green-500/10 text-green-500">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Aktiv
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-red-500/10 text-red-500">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inaktiv
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Server className="h-5 w-5 text-primary" />
                    Cloud-Verknüpfung
                  </CardTitle>
                  <CardDescription>Verbindung zu deiner Wolkenhelden Cloud</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Cloud-URL</span>
                    <div className="flex items-center gap-2">
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {currentDomainData.cloud_url || selectedDomain}
                      </code>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-8 w-8"
                        onClick={() => copyToClipboard(currentDomainData.cloud_url || selectedDomain)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">DNS-Typ</span>
                    {getRecordTypeBadge(currentDomainData.dns_record_type || 'ALIAS')}
                  </div>
                  {currentDomainData.cloud_subdomain && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Subdomain</span>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {currentDomainData.cloud_subdomain}
                      </code>
                    </div>
                  )}
                  <Button className="w-full mt-2" variant="outline" asChild>
                    <a href={`https://${currentDomainData.cloud_url || selectedDomain}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Cloud öffnen
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Nameserver
                </CardTitle>
                <CardDescription>Die Wolkenhelden Nameserver für deine Domain</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['rooti.wolkenhelden.de', 'zoni.wolkenhelden.de', 'resolvo.wolkenhelden.de', 'cachey.wolkenhelden.de'].map(ns => (
                    <div key={ns} className="flex items-center gap-2 p-2 bg-muted rounded">
                      <Server className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <code className="text-xs truncate">{ns}</code>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="h-6 w-6 ml-auto flex-shrink-0"
                        onClick={() => copyToClipboard(ns)}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DNS Records Tab */}
          <TabsContent value="dns" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">DNS-Records</CardTitle>
                  <CardDescription>Verwalte die DNS-Einträge für {selectedDomain}</CardDescription>
                </div>
                <Dialog open={addRecordOpen} onOpenChange={setAddRecordOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Record hinzufügen
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>DNS-Record hinzufügen</DialogTitle>
                      <DialogDescription>
                        Füge einen neuen DNS-Eintrag für {selectedDomain} hinzu
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Name</Label>
                          <Input 
                            placeholder="@ oder subdomain"
                            value={newRecord.name}
                            onChange={e => setNewRecord({...newRecord, name: e.target.value})}
                          />
                          <p className="text-xs text-muted-foreground">@ für Root-Domain</p>
                        </div>
                        <div className="space-y-2">
                          <Label>Typ</Label>
                          <Select 
                            value={newRecord.type} 
                            onValueChange={v => setNewRecord({...newRecord, type: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="A">A (IPv4)</SelectItem>
                              <SelectItem value="AAAA">AAAA (IPv6)</SelectItem>
                              <SelectItem value="CNAME">CNAME</SelectItem>
                              <SelectItem value="MX">MX (Mail)</SelectItem>
                              <SelectItem value="TXT">TXT</SelectItem>
                              <SelectItem value="NS">NS</SelectItem>
                              <SelectItem value="SRV">SRV</SelectItem>
                              <SelectItem value="CAA">CAA</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Wert</Label>
                        <Input 
                          placeholder={newRecord.type === 'A' ? '192.168.1.1' : 
                                      newRecord.type === 'CNAME' ? 'target.example.com' :
                                      newRecord.type === 'MX' ? 'mail.example.com' :
                                      newRecord.type === 'TXT' ? 'v=spf1 ...' : 'Wert eingeben'}
                          value={newRecord.value}
                          onChange={e => setNewRecord({...newRecord, value: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>TTL (Sekunden)</Label>
                          <Select 
                            value={String(newRecord.ttl)} 
                            onValueChange={v => setNewRecord({...newRecord, ttl: parseInt(v)})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="300">5 Minuten</SelectItem>
                              <SelectItem value="3600">1 Stunde</SelectItem>
                              <SelectItem value="14400">4 Stunden</SelectItem>
                              <SelectItem value="86400">1 Tag</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {newRecord.type === 'MX' && (
                          <div className="space-y-2">
                            <Label>Priorität</Label>
                            <Input 
                              type="number"
                              value={newRecord.pref}
                              onChange={e => setNewRecord({...newRecord, pref: parseInt(e.target.value)})}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddRecordOpen(false)}>Abbrechen</Button>
                      <Button onClick={handleAddRecord}>Hinzufügen</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {loadingRecords ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : dnsRecords.length === 0 ? (
                  <div className="text-center py-8">
                    <Info className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <p className="text-muted-foreground">Keine DNS-Records gefunden</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Füge Records hinzu um deine Domain zu konfigurieren
                    </p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Typ</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Wert</TableHead>
                        <TableHead>TTL</TableHead>
                        <TableHead className="text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dnsRecords.map((record, idx) => (
                        <TableRow key={record.id || idx} className={record.protected ? "bg-primary/5" : ""}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getRecordTypeBadge(record.type)}
                              {record.protected && (
                                <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                                  <Shield className="h-3 w-3 mr-1" />
                                  System
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <code className="text-sm">{record.name}</code>
                          </TableCell>
                          <TableCell>
                            <code className="text-sm break-all max-w-[200px] block">
                              {record.value}
                            </code>
                            {record.pref && (
                              <span className="text-xs text-muted-foreground ml-2">
                                (Prio: {record.pref})
                              </span>
                            )}
                            {record.protected && record.protected_reason && (
                              <span className="text-xs text-primary block mt-1">
                                {record.protected_reason}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{record.ttl}s</TableCell>
                          <TableCell className="text-right">
                            {record.protected ? (
                              <span className="text-xs text-muted-foreground italic">
                                Geschützt
                              </span>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className="h-8 w-8"
                                  onClick={() => {
                                    setRecordToEdit({
                                      ...record,
                                      originalName: record.name,
                                      originalType: record.type,
                                      originalValue: record.value
                                    });
                                    setEditRecordOpen(true);
                                  }}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => {
                                    setRecordToDelete(record);
                                    setDeleteRecordOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                DNS-Änderungen können bis zu 24 Stunden dauern, bis sie weltweit propagiert sind.
                Die meisten Änderungen sind jedoch innerhalb von Minuten aktiv.
              </AlertDescription>
            </Alert>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  Domain-Einstellungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1 mr-4">
                    <h4 className="font-medium">Automatische Verlängerung</h4>
                    <p className="text-sm text-muted-foreground">
                      Domain wird automatisch vor Ablauf verlängert
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {currentDomainData.auto_renew !== false ? (
                      <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Aktiv
                      </Badge>
                    ) : (
                      <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                        <XCircle className="h-3 w-3 mr-1" />
                        Inaktiv
                      </Badge>
                    )}
                    <Switch
                      data-testid="auto-renew-switch"
                      checked={currentDomainData.auto_renew !== false}
                      disabled={updatingSettings}
                      onCheckedChange={(checked) => 
                        handleUpdateDomainSettings(selectedDomain, 'auto_renew', checked)
                      }
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">WHOIS-Schutz</h4>
                    <p className="text-sm text-muted-foreground">
                      Deine persönlichen Daten werden in WHOIS-Abfragen geschützt
                    </p>
                  </div>
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                    <Shield className="h-3 w-3 mr-1" />
                    Aktiviert
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium">Transfer-Sperre</h4>
                    <p className="text-sm text-muted-foreground">
                      Verhindert unbefugte Domain-Transfers
                    </p>
                  </div>
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                    <Shield className="h-3 w-3 mr-1" />
                    Aktiviert
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Edit Record Dialog */}
      <Dialog open={editRecordOpen} onOpenChange={setEditRecordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>DNS-Record bearbeiten</DialogTitle>
          </DialogHeader>
          {recordToEdit && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input 
                    value={recordToEdit.name}
                    onChange={e => setRecordToEdit({...recordToEdit, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Typ</Label>
                  <Select 
                    value={recordToEdit.type} 
                    onValueChange={v => setRecordToEdit({...recordToEdit, type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A</SelectItem>
                      <SelectItem value="AAAA">AAAA</SelectItem>
                      <SelectItem value="CNAME">CNAME</SelectItem>
                      <SelectItem value="MX">MX</SelectItem>
                      <SelectItem value="TXT">TXT</SelectItem>
                      <SelectItem value="NS">NS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Wert</Label>
                <Input 
                  value={recordToEdit.value}
                  onChange={e => setRecordToEdit({...recordToEdit, value: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>TTL</Label>
                <Select 
                  value={String(recordToEdit.ttl)} 
                  onValueChange={v => setRecordToEdit({...recordToEdit, ttl: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="300">5 Minuten</SelectItem>
                    <SelectItem value="3600">1 Stunde</SelectItem>
                    <SelectItem value="14400">4 Stunden</SelectItem>
                    <SelectItem value="86400">1 Tag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRecordOpen(false)}>Abbrechen</Button>
            <Button onClick={handleUpdateRecord}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteRecordOpen} onOpenChange={setDeleteRecordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>DNS-Record löschen?</DialogTitle>
            <DialogDescription>
              Bist du sicher, dass du diesen DNS-Record löschen möchtest? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          {recordToDelete && (
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                {getRecordTypeBadge(recordToDelete.type)}
                <code>{recordToDelete.name}</code>
                <span className="text-muted-foreground">→</span>
                <code className="text-sm">{recordToDelete.value}</code>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRecordOpen(false)}>Abbrechen</Button>
            <Button variant="destructive" onClick={handleDeleteRecord}>Löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalDomains;
